"use client"

import { useCallback, useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Save, Key, ShieldOff, Eye, EyeOff, Loader2, CheckCircle, XCircle, Phone, Plus, ChevronDown, Bot, ArrowRight, ShoppingBag, Instagram, MessageCircle, Music2 } from "lucide-react"
import { useSystemSettings } from "@/hooks/use-system-settings"
import { useAuth } from "@/hooks/use-auth"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { LogoSettingsSection } from "./logo-settings-section"
import { ElevenLabsAccountDialog, type ElevenLabsAccountData } from "@/components/billing/elevenlabs-account-dialog"
import { DEFAULT_SCOPES as SHOPIFY_DEFAULT_SCOPES, SHOPIFY_APP_SETTING_KEYS } from "@/lib/integrations/oauth/shopify"
import { parseShopDomainList } from "@/lib/shopify/shop-domain"
import { maskSecret } from "@/lib/mask-secret"
import { useUpdateTiktokWebhookSubscriptionMutation } from "@/hooks/mutations/tiktok-webhook-subscription"

/**
 * A pool row as the admin endpoint returns it: the dialog's shape plus the
 * fields only the list needs. Extending it (rather than restating it) keeps the
 * row assignable to the edit dialog — it used to drop `webhook_name`.
 */
interface PoolAccount extends ElevenLabsAccountData {
  id: string
  status: string
}

export function SystemSettingsPage() {
  const router = useRouter()
  const { user, isLoading: authLoading, fetchWithAuth, isAuthenticated } = useAuth()
  const { settings, isLoading, updateSetting, getSetting } = useSystemSettings()

  const [googleClientId, setGoogleClientId] = useState("")
  const [googleClientSecret, setGoogleClientSecret] = useState("")
  const [showGoogleSecret, setShowGoogleSecret] = useState(false)

  // Shopify OAuth state — the single first-party PUBLIC app (App-Pricing billing +
  // catalog/attribution). `shopify_app_handle` builds the hosted plan-selection URL.
  const [shopifyClientId, setShopifyClientId] = useState("")
  const [shopifyClientSecret, setShopifyClientSecret] = useState("")
  const [shopifyScopes, setShopifyScopes] = useState("")
  const [shopifyHandle, setShopifyHandle] = useState("")
  const [shopifySignInEnabled, setShopifySignInEnabled] = useState(false)
  const [shopifyTestShops, setShopifyTestShops] = useState("")
  const [showShopifySecret, setShowShopifySecret] = useState(false)
  const [shopifySecretConfigured, setShopifySecretConfigured] = useState(false)
  const [shopifySecretHint, setShopifySecretHint] = useState("")

  // Shared Meta App OAuth state
  const [metaAppId, setMetaAppId] = useState("")
  const [metaAppSecret, setMetaAppSecret] = useState("")
  const [metaWebhookVerifyToken, setMetaWebhookVerifyToken] = useState("")
  const [metaSsoConfigId, setMetaSsoConfigId] = useState("")
  const [metaFbConfigId, setMetaFbConfigId] = useState("")
  const [metaLeadAdsConfigId, setMetaLeadAdsConfigId] = useState("")
  const [metaWhatsappConfigId, setMetaWhatsappConfigId] = useState("")
  const [showMetaSecret, setShowMetaSecret] = useState(false)
  const [metaSecretConfigured, setMetaSecretConfigured] = useState(false)

  // Instagram OAuth state (separate -IG app from Meta)
  const [instagramAppId, setInstagramAppId] = useState("")
  const [instagramAppSecret, setInstagramAppSecret] = useState("")
  const [showInstagramSecret, setShowInstagramSecret] = useState(false)
  const [instagramSecretConfigured, setInstagramSecretConfigured] = useState(false)

  // TikTok Business Messaging state (business-api.tiktok.com developer app)
  const [tiktokAppId, setTiktokAppId] = useState("")
  const [tiktokAppSecret, setTiktokAppSecret] = useState("")
  const [tiktokAuthorizationUrl, setTiktokAuthorizationUrl] = useState("")
  const [showTiktokSecret, setShowTiktokSecret] = useState(false)
  const [tiktokSecretConfigured, setTiktokSecretConfigured] = useState(false)
  const updateTiktokWebhook = useUpdateTiktokWebhookSubscriptionMutation()

  // ElevenLabs Pool accounts state
  const [poolAccounts, setPoolAccounts] = useState<PoolAccount[]>([])
  const [isLoadingPool, setIsLoadingPool] = useState(true)
  const [elDialogOpen, setElDialogOpen] = useState(false)
  const [elDialogAccount, setElDialogAccount] = useState<ElevenLabsAccountData | null>(null)

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle")
  const [statusMessage, setStatusMessage] = useState("")

  const isSuperUser = user?.role === "super_user"
  const shopifyTestShopList = parseShopDomainList(shopifyTestShops)

  const loadPoolAccounts = useCallback(async () => {
    setIsLoadingPool(true)
    try {
      const response = await fetchWithAuth("/api/admin/billing/elevenlabs/accounts")
      if (response.ok) {
        const data = await response.json()
        setPoolAccounts(data.accounts || [])
      }
    } catch (error) {
      console.error("Failed to load pool accounts:", error)
    } finally {
      setIsLoadingPool(false)
    }
  }, [fetchWithAuth])

  // Load ElevenLabs pool accounts
  useEffect(() => {
    if (isSuperUser && isAuthenticated) {
      void loadPoolAccounts()
    }
  }, [isSuperUser, isAuthenticated, loadPoolAccounts])

  useEffect(() => {
    if (!isLoading && settings.length > 0) {
      const cid = getSetting("google_client_id")
      const csec = getSetting("google_client_secret")
      setGoogleClientId(cid?.value || "")
      setGoogleClientSecret(csec?.value || "")

      // Shopify settings — the single first-party public app
      const sId = getSetting(SHOPIFY_APP_SETTING_KEYS.clientId)
      const sSec = getSetting(SHOPIFY_APP_SETTING_KEYS.clientSecret)
      const sScopes = getSetting(SHOPIFY_APP_SETTING_KEYS.scopes)
      const sHandle = getSetting(SHOPIFY_APP_SETTING_KEYS.handle)
      setShopifyClientId(sId?.value || "")
      setShopifySecretConfigured(!!sSec?.value)
      setShopifySecretHint(sSec?.value || "")
      setShopifyClientSecret("") // never pre-fill — encrypted on server
      setShopifyScopes(sScopes?.value || SHOPIFY_DEFAULT_SCOPES)
      setShopifyHandle(sHandle?.value || "")
      setShopifySignInEnabled(getSetting(SHOPIFY_APP_SETTING_KEYS.signInEnabled)?.value === "true")
      setShopifyTestShops(getSetting(SHOPIFY_APP_SETTING_KEYS.testShops)?.value || "")

      // Meta (Facebook) settings
      const mId = getSetting("meta_app_id")
      const mSec = getSetting("meta_app_secret")
      const mWebhook = getSetting("meta_webhook_verify_token")
      const mSsoConfig = getSetting("meta_sso_config_id")
      const mFbConfig = getSetting("meta_app_config_id")
      const mLeadAdsConfig = getSetting("meta_lead_ads_config_id")
      const mWaConfig = getSetting("meta_whatsapp_config_id")
      setMetaAppId(mId?.value || "")
      setMetaSecretConfigured(!!mSec?.value)
      setMetaAppSecret("") // never pre-fill — encrypted on server
      setMetaWebhookVerifyToken(mWebhook?.value || "")
      setMetaSsoConfigId(mSsoConfig?.value || "")
      setMetaFbConfigId(mFbConfig?.value || "")
      setMetaLeadAdsConfigId(mLeadAdsConfig?.value || "")
      setMetaWhatsappConfigId(mWaConfig?.value || "")

      // Instagram settings (separate -IG app)
      const igId = getSetting("instagram_app_id")
      const igSec = getSetting("instagram_app_secret")
      setInstagramAppId(igId?.value || "")
      setInstagramSecretConfigured(!!igSec?.value)
      setInstagramAppSecret("") // never pre-fill — encrypted on server

      // TikTok settings
      const ttId = getSetting("tiktok_app_id")
      const ttSec = getSetting("tiktok_app_secret")
      const ttAuthUrl = getSetting("tiktok_authorization_url")
      setTiktokAppId(ttId?.value || "")
      setTiktokSecretConfigured(!!ttSec?.value)
      setTiktokAppSecret("") // never pre-fill — encrypted on server
      setTiktokAuthorizationUrl(ttAuthUrl?.value || "")
    }
  }, [isLoading, settings, getSetting])



  if (authLoading || isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-gray-500">Loading system settings...</div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600">Please log in to access system settings.</p>
        </div>
      </div>
    )
  }

  if (!isSuperUser) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">Only super users can access system settings.</p>
          <p className="text-sm text-gray-500 mt-2"><span>Current role:</span> {user.role}</p>
        </div>
      </div>
    )
  }

  const handleSave = async (saves: Array<() => Promise<unknown>>) => {
    setSaveStatus("saving")
    try {
      for (const save of saves) {
        await save()
      }
      setSaveStatus("success")
      setStatusMessage("Settings saved successfully!")
      setTimeout(() => {
        setSaveStatus("idle")
        setStatusMessage("")
      }, 3000)
    } catch (error: unknown) {
      setSaveStatus("error")
      setStatusMessage(error instanceof Error ? error.message : "Failed to save settings")
      setTimeout(() => {
        setSaveStatus("idle")
        setStatusMessage("")
      }, 3000)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">System Settings</h1>
          <p className="text-gray-600 mt-1">Configure system-wide settings and API integrations</p>
        </div>
      </div>

      {/* AI Settings Quick Access */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
        <CardContent className="flex items-center justify-between p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">AI Configuration</h3>
              <p className="text-sm text-muted-foreground">
                Manage AI providers, models, and use case configurations
              </p>
            </div>
          </div>
          <Button
            onClick={() => router.push("?page=ai-settings")}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <span>Open AI Settings</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {statusMessage && (
        <Alert className={saveStatus === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
          <AlertDescription className={saveStatus === "success" ? "text-green-800" : "text-red-800"}>
            {statusMessage}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <LogoSettingsSection />

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    <span>Google OAuth</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="google-client-id">Google Client ID</Label>
                  <Input
                    id="google-client-id"
                    type="text"
                    placeholder="Your Google OAuth Client ID"
                    value={googleClientId}
                    onChange={(e) => setGoogleClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="google-client-secret">Google Client Secret</Label>
                  <div className="relative">
                    <Input
                      id="google-client-secret"
                      type={showGoogleSecret ? "text" : "password"}
                      placeholder="Your Google OAuth Client Secret"
                      value={googleClientSecret}
                      onChange={(e) => setGoogleClientSecret(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowGoogleSecret((v) => !v)}
                    >
                      {showGoogleSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {googleClientSecret && !showGoogleSecret && (
                    <div className="text-xs text-gray-500"><span>Current secret:</span> {maskSecret(googleClientSecret)}</div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() =>
                      handleSave([
                        () => updateSetting("google_client_id", googleClientId),
                        () => updateSetting("google_client_secret", googleClientSecret),
                      ])
                    }
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      "Saving..."
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500">
                  <span>Google Account SSO Redirect URI:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/identity/callback/google</span></code>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5" />
                    <span>Shopify OAuth</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="shopify-client-id">Shopify App Client ID</Label>
                  <Input
                    id="shopify-client-id"
                    type="text"
                    placeholder="Your Shopify App Client ID from Dev Dashboard"
                    value={shopifyClientId}
                    onChange={(e) => setShopifyClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shopify-client-secret">
                    {shopifySecretConfigured ? "Update Client Secret" : "Shopify App Client Secret"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="shopify-client-secret"
                      type={showShopifySecret ? "text" : "password"}
                      placeholder={shopifySecretConfigured ? "Enter new secret to update..." : "Your Shopify App Client Secret"}
                      value={shopifyClientSecret}
                      onChange={(e) => setShopifyClientSecret(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowShopifySecret((v) => !v)}
                    >
                      {showShopifySecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {shopifySecretConfigured && !shopifyClientSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span>Saved (encrypted):</span> <code className="bg-gray-100 px-1 rounded text-gray-600">{shopifySecretHint}</code>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shopify-scopes">OAuth Scopes</Label>
                  <Input
                    id="shopify-scopes"
                    type="text"
                    placeholder="read_orders,read_customers,..."
                    value={shopifyScopes}
                    onChange={(e) => setShopifyScopes(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    <span>Comma-separated. Add</span> <code className="bg-gray-100 px-1 rounded">read_all_orders</code> <span>after Shopify approval for full order history access.</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shopify-handle">App Handle</Label>
                  <Input
                    id="shopify-handle"
                    type="text"
                    placeholder="your-app-handle"
                    value={shopifyHandle}
                    onChange={(e) => setShopifyHandle(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    <span>The app&apos;s Shopify handle — the</span> <code className="bg-gray-100 px-1 rounded">&lt;handle&gt;</code> <span>in
                    the hosted plan URL</span> <code className="bg-gray-100 px-1 rounded">.../charges/&lt;handle&gt;/pricing_plans</code><span>. Must match exactly.</span>
                  </p>
                </div>

                <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                  <div className="min-w-0 space-y-1">
                    <Label htmlFor="shopify-sign-in-enabled">Shopify sign-in</Label>
                    <p className="text-xs text-gray-500">
                      Store owners opening the app from their Shopify admin are signed in to Versafy, and a new
                      install claims its account with the owner email Shopify verified, without a password. The
                      sign-in form shows a Shopify button. Turn off and save to restore the plain redirect and the
                      password claim and to hide the button; pending sign-ins stop too. While off, it stays on for the
                      test stores below, without the button.
                    </p>
                  </div>
                  <Switch
                    id="shopify-sign-in-enabled"
                    checked={shopifySignInEnabled}
                    onCheckedChange={setShopifySignInEnabled}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shopify-test-shops">Test stores</Label>
                  <Textarea
                    id="shopify-test-shops"
                    rows={3}
                    placeholder="demo-shop.myshopify.com"
                    value={shopifyTestShops}
                    onChange={(e) => setShopifyTestShops(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    One <code className="bg-gray-100 px-1 rounded">*.myshopify.com</code> domain per line. Shopify
                    switches that are off, such as Shopify sign-in, stay on for these stores, so a release can be
                    checked before every merchant gets it.
                  </p>
                  {shopifyTestShopList.invalid.length > 0 && (
                    <p className="text-xs text-red-600 break-words">
                      {`Not a *.myshopify.com domain: ${shopifyTestShopList.invalid.join(", ")}`}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.clientId, shopifyClientId),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.scopes, shopifyScopes),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.handle, shopifyHandle),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.signInEnabled, String(shopifySignInEnabled)),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.testShops, shopifyTestShopList.shops.join("\n")),
                      ]
                      // Only update secret if a new value was entered
                      if (shopifyClientSecret.trim()) {
                        saves.push(() => updateSetting(SHOPIFY_APP_SETTING_KEYS.clientSecret, shopifyClientSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving" || shopifyTestShopList.invalid.length > 0}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      "Saving..."
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500">
                  <span>Callback URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/integrations/shopify/oauth/callback</span></code>
                </div>
                <p className="text-xs text-gray-500">
                  <span>Client Secret is stored encrypted (AES-256-GCM). Create the app in</span>{" "}
                  <a href="https://partners.shopify.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Shopify Partners Dashboard
                  </a>{" "}
                  <span>with Public Distribution (Limited Visibility).</span>
                </p>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>


        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    <span>Meta — Facebook Messenger &amp; WhatsApp</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="meta-app-id">Meta App ID</Label>
                  <Input
                    id="meta-app-id"
                    type="text"
                    placeholder="Your Meta App ID from Developer Portal"
                    value={metaAppId}
                    onChange={(e) => setMetaAppId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-app-secret">
                    {metaSecretConfigured ? "Update App Secret" : "Meta App Secret"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="meta-app-secret"
                      type={showMetaSecret ? "text" : "password"}
                      placeholder={metaSecretConfigured ? "Enter new secret to update..." : "Your Meta App Secret"}
                      value={metaAppSecret}
                      onChange={(e) => setMetaAppSecret(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowMetaSecret((v) => !v)}
                    >
                      {showMetaSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {metaSecretConfigured && !metaAppSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span>App secret configured (encrypted)</span>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-webhook-verify-token">Webhook Verify Token</Label>
                  <Input
                    id="meta-webhook-verify-token"
                    type="text"
                    placeholder="Custom string for webhook verification"
                    value={metaWebhookVerifyToken}
                    onChange={(e) => setMetaWebhookVerifyToken(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    Shared by the Facebook, WhatsApp and Instagram webhooks. Must match the Verify Token in Meta Developer Portal.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-sso-config-id">Facebook account SSO — Config ID</Label>
                  <Input
                    id="meta-sso-config-id"
                    type="text"
                    placeholder="Configuration ID for email and pages_show_list"
                    value={metaSsoConfigId}
                    onChange={(e) => setMetaSsoConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    Dedicated Facebook Login for Business user-token configuration for Versafy account sign-in. Kept separate from Page, Lead Ads, and WhatsApp configurations.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-fb-config-id">FB Login for Business — Config ID</Label>
                  <Input
                    id="meta-fb-config-id"
                    type="text"
                    placeholder="Configuration ID from Facebook Login for Business"
                    value={metaFbConfigId}
                    onChange={(e) => setMetaFbConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    Create in Meta App Dashboard → Facebook Login for Business → Configurations.
                    Uses config_id instead of scopes for reliable page access delegation.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-whatsapp-config-id">WhatsApp Embedded Signup — Config ID</Label>
                  <Input
                    id="meta-whatsapp-config-id"
                    type="text"
                    placeholder="Configuration ID from WhatsApp Embedded Signup"
                    value={metaWhatsappConfigId}
                    onChange={(e) => setMetaWhatsappConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span>Meta App Dashboard → Facebook Login for Business → Configurations → login variation</span>
                    <strong> WhatsApp Embedded Signup</strong><span>. Powers the &quot;Connect WhatsApp&quot; tile;
                    while empty, the tile stays disabled.</span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meta-lead-ads-config-id">Meta Lead Ads — Config ID</Label>
                  <Input
                    id="meta-lead-ads-config-id"
                    type="text"
                    placeholder="User access-token configuration ID for Lead Ads"
                    value={metaLeadAdsConfigId}
                    onChange={(e) => setMetaLeadAdsConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span>Dedicated Facebook Login for Business configuration with Page selection and</span>
                    <code> pages_show_list</code><span>,</span> <code>pages_read_engagement</code><span>,</span>
                    <code> pages_manage_metadata</code><span>, and</span> <code>leads_retrieval</code><span>.</span>
                  </p>
                </div>

                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                  <p className="text-xs font-medium text-gray-700">
                    Required webhook fields in the Meta App Dashboard
                  </p>
                  <p className="text-xs text-gray-500">
                    Subscribing the callback URL is not enough — each field has to be checked
                    explicitly, or the webhook stays silent.
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>
                      <strong>Messenger</strong> <span>→</span> <code>/api/webhooks/messaging/facebook</code><span>:</span>{" "}
                      <code>messages</code><span>,</span> <code>messaging_postbacks</code><span>,</span>{" "}
                      <code>message_reactions</code><span>,</span> <code>message_echoes</code><span>; Meta Ads also
                      uses this Page callback with</span> <code>leadgen</code><span>.</span>
                    </p>
                    <p>
                      <strong>WhatsApp</strong> <span>→</span> <code>/api/webhooks/messaging/whatsapp</code><span>:</span>{" "}
                      <code>messages</code><span>,</span> <code>account_update</code><span>,</span>{" "}
                      <code>smb_message_echoes</code><span>,</span> <code>history</code><span>,</span>{" "}
                      <code>smb_app_state_sync</code>
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">
                    <code>message_echoes</code> <span>is what surfaces operator replies sent from the Page
                    inbox. On WhatsApp the app-level subscription alone delivers nothing — connecting
                    a channel also subscribes the app to that customer&apos;s WABA, and both are
                    required.</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    <span>The last three WhatsApp fields only ever fire for numbers connected through
                    Coexistence:</span> <code>smb_message_echoes</code> <span>carries replies the business sent
                    from the WhatsApp Business app, and the other two carry the one-time chat and
                    contact import.</span> <code>account_update</code> <span>is how Meta reports that a customer
                    disconnected Cloud API — without it a dead channel keeps reading as connected.</span>
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting("meta_app_id", metaAppId),
                        () => updateSetting("meta_webhook_verify_token", metaWebhookVerifyToken),
                        () => updateSetting("meta_sso_config_id", metaSsoConfigId),
                        () => updateSetting("meta_app_config_id", metaFbConfigId),
                        () => updateSetting("meta_lead_ads_config_id", metaLeadAdsConfigId),
                        () => updateSetting("meta_whatsapp_config_id", metaWhatsappConfigId),
                      ]
                      if (metaAppSecret.trim()) {
                        saves.push(() => updateSetting("meta_app_secret", metaAppSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      "Saving..."
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <span>Facebook Callback URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/agents/messaging/channels/facebook/callback</span></code>
                  </p>
                  <p>
                    <span>Facebook Account SSO Redirect URI:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/identity/callback/facebook</span></code>
                  </p>
                  <p>
                    <span>Meta Lead Ads Callback URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/integrations/meta-ads/oauth/callback</span></code>
                  </p>
                  <p>
                    <span>Facebook Webhook URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/webhooks/messaging/facebook</span></code>
                  </p>
                  <p>
                    <span>WhatsApp Webhook URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/webhooks/messaging/whatsapp</span></code>
                    {" "}<span>— set in WhatsApp → Configuration, subscribe the</span> <code className="bg-gray-100 px-1 rounded">messages</code> <span>field. Serves every connected number; no callback URL needed.</span>
                  </p>
                  <p>
                    <span>App Secret is stored encrypted (AES-256-GCM). Create the app in</span>{" "}
                    <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Meta Developer Portal
                    </a><span>.</span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Instagram className="w-5 h-5" />
                    <span>Instagram OAuth</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="instagram-app-id">Instagram App ID</Label>
                  <Input
                    id="instagram-app-id"
                    type="text"
                    placeholder="App ID from the Instagram -IG app"
                    value={instagramAppId}
                    onChange={(e) => setInstagramAppId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram-app-secret">
                    {instagramSecretConfigured ? "Update App Secret" : "Instagram App Secret"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="instagram-app-secret"
                      type={showInstagramSecret ? "text" : "password"}
                      placeholder={instagramSecretConfigured ? "Enter new secret to update..." : "Your Instagram App Secret"}
                      value={instagramAppSecret}
                      onChange={(e) => setInstagramAppSecret(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowInstagramSecret((v) => !v)}
                    >
                      {showInstagramSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {instagramSecretConfigured && !instagramAppSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span>App secret configured (encrypted)</span>
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting("instagram_app_id", instagramAppId),
                      ]
                      if (instagramAppSecret.trim()) {
                        saves.push(() => updateSetting("instagram_app_secret", instagramAppSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      "Saving..."
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <span>Callback URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/agents/messaging/channels/instagram/callback</span></code>
                  </p>
                  <p>
                    <span>Webhook URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/webhooks/messaging/instagram</span></code>
                  </p>
                  <p>
                    <span>Meta creates a separate Instagram app with</span> <code className="bg-gray-100 px-1 rounded">-IG</code> <span>suffix when you configure &ldquo;API setup with Instagram Login&rdquo; in the</span>{" "}
                    <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Meta Developer Portal
                    </a><span>.
                    Use the App ID and Secret from that</span> <code className="bg-gray-100 px-1 rounded">-IG</code> <span>app (not the main one).
                    Webhook Verify Token is shared — configure it in the Meta / Facebook Messenger section above.</span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Music2 className="w-5 h-5" />
                    <span>TikTok Business Messaging</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="tiktok-app-id">TikTok App ID</Label>
                  <Input
                    id="tiktok-app-id"
                    type="text"
                    placeholder="App ID from My Apps → App Detail → Basic Information"
                    value={tiktokAppId}
                    onChange={(e) => setTiktokAppId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tiktok-app-secret">
                    {tiktokSecretConfigured ? "Update App Secret" : "TikTok App Secret"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="tiktok-app-secret"
                      type={showTiktokSecret ? "text" : "password"}
                      placeholder={tiktokSecretConfigured ? "Enter new secret to update..." : "App Secret from App Detail → Basic Information"}
                      value={tiktokAppSecret}
                      onChange={(e) => setTiktokAppSecret(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowTiktokSecret((v) => !v)}
                    >
                      {showTiktokSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {tiktokSecretConfigured && !tiktokAppSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span>App secret configured (encrypted)</span>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tiktok-authorization-url">Account Holder Authorization URL</Label>
                  <Input
                    id="tiktok-authorization-url"
                    type="text"
                    placeholder="https://www.tiktok.com/v2/auth/authorize?..."
                    value={tiktokAuthorizationUrl}
                    onChange={(e) => setTiktokAuthorizationUrl(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting("tiktok_app_id", tiktokAppId),
                        () => updateSetting("tiktok_authorization_url", tiktokAuthorizationUrl),
                      ]
                      if (tiktokAppSecret.trim()) {
                        saves.push(() => updateSetting("tiktok_app_secret", tiktokAppSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      "Saving..."
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>

                {/* The webhook subscription has no dashboard UI at TikTok — it
                    exists only as an API call, and the call needs the app secret,
                    so it runs server-side. One mutable callback URL per app: from
                    a non-production deployment this steals production's traffic. */}
                <div className="flex items-center justify-between gap-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs text-amber-900">
                    <div className="font-medium">Webhook subscription</div>
                    <div>
                      Points this TikTok app&apos;s Direct Message webhook at this deployment.
                      TikTok stores one callback URL per app, so running this here takes over
                      webhook delivery for every business that authorized the app — including
                      production&apos;s.
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => updateTiktokWebhook.mutate()}
                    disabled={updateTiktokWebhook.isPending}
                  >
                    {updateTiktokWebhook.isPending ? "Updating..." : "Point webhook here"}
                  </Button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <span>Redirect URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/agents/messaging/channels/tiktok/callback</span></code>
                  </p>
                  <p>
                    <span>Webhook URL:</span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span>/api/webhooks/messaging/tiktok</span></code>
                  </p>
                  <p>
                    <span>All three values come from My Apps → App Detail → Basic Information in the</span>{" "}
                    <a href="https://business-api.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      TikTok for Business developer portal
                    </a>{" "}
                    <span>(not developers.tiktok.com — that is the creator portal and has no messaging product).
                    App Secret is stored encrypted and doubles as the webhook signature key.</span>
                  </p>
                </div>

                {/* The app's scope selection is not visible anywhere at runtime, and a
                    missing one costs another review cycle to add. */}
                <div className="rounded-md border p-3 text-xs text-gray-600 space-y-2">
                  <div className="font-medium text-gray-900">App permissions — Scope of permission</div>
                  <ul className="space-y-1">
                    <li>
                      <span className="font-medium">TikTok Accounts</span> <span>→ Auth Code Management (token
                      exchange), Account User (profile probe). Leave post, comment and media
                      sub-permissions off — unused, and irrelevant scopes draw review questions.</span>
                    </li>
                    <li>
                      <span className="font-medium">Ad Account Management</span><span>,</span>{" "}
                      <span className="font-medium">CTX Events Management</span><span>,</span>{" "}
                      <span className="font-medium">Measurement</span> <span>— unused by us, but TikTok
                      requires all three on the app before it accepts a Business Messaging request.</span>
                    </li>
                  </ul>
                  <p>
                    Business Messaging is not selectable — it is granted by review and attaches to the
                    approved app id. The authorization URL above appears only after that.
                  </p>
                  <p>
                    <span>TikTok Accounts also requires the</span>{" "}
                    <a href="https://bytedance.sg.larkoffice.com/share/base/form/shrlgu4WEvtSXpEDLcCw56u4Rfc" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Accounts API Access Application Form
                    </a>{" "}
                    <span className="font-medium">before</span> <span>the app is submitted; skipping it gets
                    both approvals rejected.</span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    <span>Voice AI Providers</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">ElevenLabs Accounts</h3>
                  <Button
                    size="sm"
                    onClick={() => { setElDialogAccount(null); setElDialogOpen(true) }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    <span>Add Account</span>
                  </Button>
                </div>

                {isLoadingPool ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading accounts...</span>
                  </div>
                ) : poolAccounts.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    No ElevenLabs accounts configured. Add one to get started.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {poolAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="border rounded-lg p-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{account.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {account.api_key_masked}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {account.status === "active" ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-destructive" />
                            )}
                            {account.webhook_id ? (
                              <span className="text-xs text-green-600">Webhook OK</span>
                            ) : (
                              <span className="text-xs text-yellow-600">No webhook</span>
                            )}
                            {account.is_default && (
                              <span className="text-xs bg-muted px-1.5 py-0.5 rounded">default</span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setElDialogAccount(account)
                            setElDialogOpen(true)
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <ElevenLabsAccountDialog
                  open={elDialogOpen}
                  onOpenChange={setElDialogOpen}
                  account={elDialogAccount}
                  onSaved={loadPoolAccounts}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </div>
  )
}
